import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { fetchAllPosts, createPost, updatePost, deletePost } from "../services/firestore";
import "./AdminPage.css";
import {supabase} from "../supabaseClient";

const CATEGORIES = ["Politics","Entertainment","Sports","Business","Technology","Security","Education","Health","Viral","Trending"];

const BLANK_FORM = {
  title: "",
  excerpt: "",
  content: "",
  category: "Politics",
  imageUrl: "",
  author: "",
  trending: false,
  viral: false,
};

/* ── SVG Icons ─────────────────────────────────────────────────── */
const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);
const EditIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);
const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
    <path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
  </svg>
);
const EyeIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
  </svg>
);
const LogoutIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
    <polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
  </svg>
);
const ArticleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
    <polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/>
  </svg>
);
const CloseIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

/* ── Helpers ───────────────────────────────────────────────────── */
function formatDate(ts) {
  if (!ts) return "—";
  const d = ts?.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

/* ── Stat card ─────────────────────────────────────────────────── */
function StatCard({ label, value, sub, color }) {
  return (
    <div className="stat-card">
      <div className="stat-value" style={{ color }}>{value}</div>
      <div className="stat-label">{label}</div>
      {sub && <div className="stat-sub">{sub}</div>}
    </div>
  );
}

/* ── Post form (create / edit) ─────────────────────────────────── */
function PostForm({ initial, onSave, onClose, saving }) {
  const [form, setForm] = useState(initial || BLANK_FORM);

  const set = (field, val) => setForm(f => ({ ...f, [field]: val }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.content.trim()) return;
    onSave(form);
  };

  const [uploading, setUploading] = useState(false);
  const [imageUrl, setImageUrl] = useState('');

  const handleUpload = async (event) => {
    try {
      setUploading(true);

      const file = event.target.files[0];
      if (!file) return;

      // 1. Generate a unique name for the file to prevent overwrites
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2, 7)}.${fileExt}`;
      const filePath = `blog-posts/${fileName}`;

      // 2. Upload the file to your 'NIGERIAPUBLISHERS' bucket
      const { data, error } = await supabase.storage
        .from('nigeriapublishers')
        .upload(filePath, file);

      if (error) {
        throw error;
      }

      // 3. Get the public URL for your blog image
      const { data: publicUrlData } = supabase.storage
        .from('nigeriapublishers')
        .getPublicUrl(filePath);

      const finalUrl = publicUrlData.publicUrl;
      setImageUrl(finalUrl);
      set("imageUrl", finalUrl);
      console.log("Uploaded successfully! Image URL:", finalUrl);

      // PRO-TIP: You can now save 'finalUrl' to your Firebase database here!
      // Example: updateDoc(docRef, { blogImage: finalUrl });

    } catch (error) {
      alert('Error uploading image: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{initial ? "Edit Article" : "New Article"}</h2>
          <button className="modal-close" onClick={onClose}><CloseIcon /></button>
        </div>

        <form className="post-form" onSubmit={handleSubmit}>
          <div className="form-row">
            <label>Title *</label>
            <input
              value={form.title}
              onChange={e => set("title", e.target.value)}
              placeholder="Article headline…"
              required
            />
          </div>

          <div className="form-row-2col">
            <div className="form-row">
              <label>Category</label>
              <select value={form.category} onChange={e => set("category", e.target.value)}>
                {CATEGORIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div className="form-row">
              <label>Author</label>
              <input
                value={form.author}
                onChange={e => set("author", e.target.value)}
                placeholder="Author name"
              />
            </div>
          </div>

          <div className="form-row">
            <label>Cover Image URL</label>
            <input
              value={form.imageUrl}
              onChange={e => set("imageUrl", e.target.value)}
              placeholder="https://…"
              type="url"
            />
            <input 
        type="file" 
        accept="image/*" 
        onChange={handleUpload} 
        disabled={uploading} 
      />

      {uploading && <p>Uploading picture from your device...</p>}
            {form.imageUrl && (
              <img src={form.imageUrl} alt="preview" className="img-preview" />
            )}
          </div>

          <div className="form-row">
            <label>Excerpt / Summary</label>
            <textarea
              value={form.excerpt}
              onChange={e => set("excerpt", e.target.value)}
              placeholder="Short summary shown on article cards…"
              rows={2}
            />
          </div>

          <div className="form-row">
            <label>Content * (HTML supported)</label>
            <textarea
              value={form.content}
              onChange={e => set("content", e.target.value)}
              placeholder="<p>Article body…</p>"
              rows={10}
              required
            />
          </div>

          <div className="form-checkboxes">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={form.trending}
                onChange={e => set("trending", e.target.checked)}
              />
              <span>Mark as Trending</span>
            </label>
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={form.viral}
                onChange={e => set("viral", e.target.checked)}
              />
              <span>Mark as Viral</span>
            </label>
          </div>

          <div className="form-actions">
            <button type="button" className="btn-cancel" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-publish" disabled={saving}>
              {saving ? "Saving…" : initial ? "Save Changes" : "Publish Article"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ── Main AdminPage ────────────────────────────────────────────── */
export default function AdminPage() {
  const { user, logout, loading } = useAuth();
  const navigate = useNavigate();

  const [posts, setPosts]       = useState([]);
  const [fetching, setFetching] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing]   = useState(null); // post object being edited
  const [saving, setSaving]     = useState(false);
  const [deleting, setDeleting] = useState(null); // id being deleted
  const [toast, setToast]       = useState("");
  const [search, setSearch]     = useState("");
  const [catFilter, setCatFilter] = useState("All");

  /* Auth guard */
  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [user, loading, navigate]);

  /* Load posts */
  const loadPosts = () => {
    setFetching(true);
    fetchAllPosts().then(data => {
      setPosts(data);
      setFetching(false);
    });
  };

  useEffect(() => { if (user) loadPosts(); }, [user]);

  /* Toast helper */
  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 3000);
  };

  /* Create */
  const handleCreate = async (form) => {
    setSaving(true);
    try {
      await createPost(form);
      showToast("Article published!");
      setShowForm(false);
      loadPosts();
    } catch (err) {
      showToast("Error: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  /* Update */
  const handleUpdate = async (form) => {
    setSaving(true);
    try {
      await updatePost(editing.id, form);
      showToast("Article updated!");
      setEditing(null);
      loadPosts();
    } catch (err) {
      showToast("Error: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  /* Delete */
  const handleDelete = async (id) => {
    setDeleting(id);
    try {
      await deletePost(id);
      showToast("Article deleted.");
      setPosts(p => p.filter(x => x.id !== id));
    } catch (err) {
      showToast("Error: " + err.message);
    } finally {
      setDeleting(null);
    }
  };

  /* Filtered view */
  const filtered = posts.filter(p => {
    const matchCat = catFilter === "All" || p.category === catFilter;
    const matchQ   = !search || p.title?.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchQ;
  });

  const trendingCount = posts.filter(p => p.trending).length;
  const viralCount    = posts.filter(p => p.viral).length;

  if (loading || !user) return null;

  return (
    <div className="admin-page">

      {/* ── Sidebar ── */}
      <aside className="admin-sidebar">
        <div className="admin-brand">
          <Link to="/" className="admin-brand-link">
            <span className="admin-brand-top">Nigeria</span>
            <span className="admin-brand-bottom">Publishers</span>
          </Link>
          <span className="admin-badge">Admin</span>
        </div>

        <nav className="admin-nav">
          <button className="admin-nav-item active">
            <ArticleIcon /> Articles
          </button>
        </nav>

        <div className="admin-user">
          <div className="admin-user-avatar">
            {user.email[0].toUpperCase()}
          </div>
          <div className="admin-user-info">
            <span className="admin-user-email">{user.email}</span>
            <span className="admin-user-role">Administrator</span>
          </div>
          <button className="admin-logout" onClick={() => { logout(); navigate("/"); }} title="Logout">
            <LogoutIcon />
          </button>
        </div>
      </aside>

      {/* ── Main content ── */}
      <div className="admin-main">

        {/* Header */}
        <div className="admin-topbar">
          <div>
            <h1 className="admin-heading">Articles</h1>
            <p className="admin-subheading">Manage all published content</p>
          </div>
          <button className="btn-new" onClick={() => setShowForm(true)}>
            <PlusIcon /> New Article
          </button>
        </div>

        {/* Stats */}
        <div className="stats-row">
          <StatCard label="Total Articles" value={posts.length} color="#111827" />
          <StatCard label="Trending" value={trendingCount} color="#f59e0b" />
          <StatCard label="Viral" value={viralCount} color="#e63946" />
          <StatCard label="Categories" value={CATEGORIES.length} color="#6366f1" />
        </div>

        {/* Filters */}
        <div className="admin-filters">
          <input
            className="admin-search"
            placeholder="Search articles…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <div className="cat-pills">
            {["All", ...CATEGORIES].map(c => (
              <button
                key={c}
                className={`cat-pill${catFilter === c ? " active" : ""}`}
                onClick={() => setCatFilter(c)}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        {fetching ? (
          <div className="admin-loading">
            <div className="loading-spinner" />
            <span>Loading articles…</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="admin-empty">
            <p>No articles found.</p>
          </div>
        ) : (
          <div className="posts-table-wrap">
            <table className="posts-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Category</th>
                  <th>Author</th>
                  <th>Date</th>
                  <th>Flags</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(post => (
                  <tr key={post.id}>
                    <td className="td-title">
                      {post.imageUrl && (
                        <img src={post.imageUrl} alt="" className="table-thumb" />
                      )}
                      <span>{post.title}</span>
                    </td>
                    <td>
                      <span className="cat-badge">{post.category}</span>
                    </td>
                    <td className="td-author">{post.author || "—"}</td>
                    <td className="td-date">{formatDate(post.timestamp)}</td>
                    <td className="td-flags">
                      {post.trending && <span className="flag flag-trending">Trending</span>}
                      {post.viral    && <span className="flag flag-viral">Viral</span>}
                    </td>
                    <td className="td-actions">
                      <Link
                        to={`/post/${post.id}`}
                        target="_blank"
                        className="action-btn action-view"
                        title="View"
                      >
                        <EyeIcon />
                      </Link>
                      <button
                        className="action-btn action-edit"
                        onClick={() => setEditing(post)}
                        title="Edit"
                      >
                        <EditIcon />
                      </button>
                      <button
                        className="action-btn action-delete"
                        onClick={() => handleDelete(post.id)}
                        disabled={deleting === post.id}
                        title="Delete"
                      >
                        {deleting === post.id
                          ? <span className="mini-spin" />
                          : <TrashIcon />
                        }
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Modals ── */}
      {showForm && (
        <PostForm
          onSave={handleCreate}
          onClose={() => setShowForm(false)}
          saving={saving}
        />
      )}
      {editing && (
        <PostForm
          initial={editing}
          onSave={handleUpdate}
          onClose={() => setEditing(null)}
          saving={saving}
        />
      )}

      {/* ── Toast ── */}
      {toast && <div className="admin-toast">{toast}</div>}
    </div>
  );
}
